import type { ButtonHTMLAttributes, ReactNode } from "react";

import { Icon, type IconName } from "./Icon";

type Variant = "primary" | "default" | "subtle" | "ghost" | "accent" | "danger";
type Size = "xs" | "sm" | "md" | "lg";

export interface Btn2Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "size"> {
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  iconRight?: IconName;
  full?: boolean;
  active?: boolean;
  children?: ReactNode;
}

const SIZE: Record<Size, { h: string; px: string; text: string; gap: string; iconSize: number }> = {
  xs: { h: "h-[22px]", px: "px-2", text: "text-[11px]", gap: "gap-1", iconSize: 12 },
  sm: { h: "h-[26px]", px: "px-2.5", text: "text-[12px]", gap: "gap-[5px]", iconSize: 13 },
  md: { h: "h-[30px]", px: "px-3", text: "text-[13px]", gap: "gap-1.5", iconSize: 14 },
  lg: { h: "h-9", px: "px-4", text: "text-[14px]", gap: "gap-[7px]", iconSize: 15 },
};

const VARIANT: Record<Variant, string> = {
  primary: "bg-n-900 text-white border-n-900 shadow-1 hover:bg-n-800",
  default: "bg-white text-n-800 border-n-200 shadow-1 hover:bg-n-50",
  subtle: "bg-n-100 text-n-700 border-transparent hover:bg-n-150",
  ghost: "bg-transparent text-n-700 border-transparent hover:bg-n-100",
  accent: "bg-a-500 text-white border-a-600 shadow-1 hover:bg-a-600",
  danger: "bg-white text-bad border-n-200 shadow-1 hover:bg-bad-bg",
};

export function Btn2({
  variant = "default",
  size = "md",
  icon,
  iconRight,
  full,
  active,
  children,
  className = "",
  type = "button",
  ...rest
}: Btn2Props): ReactNode {
  const s = SIZE[size];
  const activeCls = active ? "!bg-n-800 !text-white !border-n-800" : "";
  const widthCls = full ? "w-full" : "";
  return (
    <button
      {...rest}
      type={type}
      className={`inline-flex items-center justify-center rounded-2 border font-medium tracking-[-0.05px] whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${s.h} ${s.px} ${s.text} ${s.gap} ${VARIANT[variant]} ${activeCls} ${widthCls} ${className}`}
    >
      {icon && <Icon name={icon} size={s.iconSize} />}
      {children}
      {iconRight && <Icon name={iconRight} size={s.iconSize} />}
    </button>
  );
}
